// src/lib/apiFetch.js
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE = process.env.REACT_APP_API_BASE; // e.g. https://.../prod

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
function isBodyMethod(method) {
  const m = method.toUpperCase();
  return m !== 'GET' && m !== 'HEAD';
}
function logGroup(label, fn) {
  try {
    console.groupCollapsed?.(label);
    fn();
  } finally {
    console.groupEnd?.();
  }
}

async function buildHeaders(method, { withAuth = true, extra = {} } = {}) {
  const headers = {
    Accept: 'application/json',                 // harmless on all requests
    ...(isBodyMethod(method) ? { 'Content-Type': 'application/json' } : {}), // avoid preflight on GET
    ...extra,
  };

  if (withAuth) {
    const { tokens } = await fetchAuthSession();
    const idToken = tokens?.idToken?.toString();
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
  }
  return headers;
}

function resolveUrl(path) {
  // Allow absolute URLs for one-off calls; otherwise prefix with API_BASE
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) throw new Error('REACT_APP_API_BASE is not set');
  return `${API_BASE}${path}`;
}

async function request(
  method,
  path,
  body,
  {
    retries = 2,
    backoffBase = 300,
    withAuth = true,
    extraHeaders = {},
    log = true,
    timeoutMs = 15000,
  } = {}
) {
  const url = resolveUrl(path);
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const headers = await buildHeaders(method, { withAuth, extra: extraHeaders });

      // Optional timeout to surface network stalls clearly
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort('timeout'), timeoutMs);

      const t0 = performance.now?.() ?? Date.now();
      const res = await fetch(url, {
        method,
        headers,
        body: isBodyMethod(method) && body != null ? JSON.stringify(body) : undefined,
        mode: 'cors',
        credentials: 'omit',
        signal: ctrl.signal,
      });
      clearTimeout(t);
      const t1 = performance.now?.() ?? Date.now();

      // Read raw first so we can both log and parse
      const raw = await res.text().catch(() => '');

      if (log) {
        logGroup(`[apiFetch] ${method} ${path} — ${res.status} ${res.statusText}`, () => {
          console.info('url:', url);
          console.info('durationMs:', Math.round(t1 - t0));
          console.info('ok:', res.ok);
          console.info('content-type:', res.headers.get('content-type'));
          console.info('raw (first 400):', raw.slice(0, 400));
        });
      }

      // If not OK, try to parse server JSON so callers get code/details
      if (!res.ok) {
        let server;
        try { server = JSON.parse(raw); } catch {}
        const err = new Error(server?.message || `HTTP ${res.status} ${res.statusText}`);
        err.status = res.status;
        if (server && typeof server === 'object') {
          err.code = server.code;
          err.details = server.details;
          err.server = server;
        }
        throw err;
      }

      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (ct.includes('application/json')) {
        try {
          return JSON.parse(raw);
        } catch (e) {
          const err = new Error(`JSON parse error: ${e.message}`);
          err.status = res.status;
          throw err;
        }
      }
      return raw; // Non-JSON fallback
    } catch (e) {
      lastError = e;
      const status = e?.status;
      const isTransient = !status || (status >= 500 && status < 600) || status === 429;

      if (log) {
        logGroup(`[apiFetch] ${method} ${path} — error`, () => {
          console.error(e);
          console.info('status:', status, 'transient:', !!isTransient, 'attempt:', attempt);
        });
      }

      if (attempt === retries || !isTransient) break;
      await sleep(backoffBase * (attempt + 1)); // 300ms, 600ms, 900ms…
    }
  }

  throw lastError;
}

export const api = {
  get: (path, opts) => request('GET', path, undefined, opts),
  post: (path, body, opts) => request('POST', path, body, opts),
  put: (path, body, opts) => request('PUT', path, body, opts),
  del: (path, opts) => request('DELETE', path, undefined, opts),
};
